import { useEffect, useState, useRef } from "react";
import {
  collection, addDoc, getDocs, doc, deleteDoc, query, where,
  updateDoc, arrayUnion, arrayRemove, serverTimestamp, orderBy
} from "firebase/firestore";
import { db } from "../firebase.js";
import { checkAndAssignBadges } from "../utils/badgeUtils.js";
import { createNotification } from "../utils/notificationUtils.js";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { containsBadWord } from "../utils/badWords.js";
import { toast } from "sonner";
import "../styles/ForumPageModern.css";
import Button from "../components/ui/Button";
import Avatar from "../components/Avatar";

const CATEGORIES = ["Tümü", "TYT", "AYT", "Rehberlik", "Soru Çözümü", "Sohbet"];

const formatDateTime = (dateInput) => {
  if (!dateInput) return "";
  let date = dateInput.toDate ? dateInput.toDate() : new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("tr-TR") + " " + date.toLocaleTimeString("tr-TR", { hour: '2-digit', minute: '2-digit' });
};

function ForumPage() {
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const isLoggedIn = !!userId;

  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState({});
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState("list"); // 'list' | 'topic'
  const [selectedTopic, setSelectedTopic] = useState(null);

  // New Topic Form
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [newTopic, setNewTopic] = useState({ title: "", content: "", category: "Sohbet" });

  // New Comment Form
  const [newCommentText, setNewCommentText] = useState("");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchPosts();
  }, [userId]);

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const uData = {};
      snap.forEach(u => uData[u.id] = u.data());
      setUsers(uData);
    } catch (err) { console.error(err); }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "forumPosts"), orderBy("timestamp", "desc"));
      const snap = await getDocs(q);
      const postIds = snap.docs.map(d => d.id);
      
      const pData = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          title: data.title || (data.text ? data.text.substring(0, 30) + '...' : 'Başlıksız Konu'),
          category: data.category || "Sohbet",
          commentCount: data.commentCount || 0,
          likes: data.likedBy?.length || 0,
        };
      });
      setPosts(pData);
    } catch (err) { toast.error("Gönderiler yüklenemedi."); }
    setLoading(false);
  };

  const fetchCommentsForTopic = async (topicId) => {
    try {
      const q = query(collection(db, "comments"), where("postId", "==", topicId));
      const snap = await getDocs(q);
      const comments = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (a.createdAt?.toDate?.() || 0) - (b.createdAt?.toDate?.() || 0));
      return comments;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const handleOpenTopic = async (post) => {
    setSelectedTopic({ ...post, comments: [] });
    setView("topic");
    const comments = await fetchCommentsForTopic(post.id);
    setSelectedTopic(prev => ({ ...prev, comments }));
  };

  const handleCreateTopic = async () => {
    if (!isLoggedIn) return toast.error("Giriş yapmalısınız!");
    if (!newTopic.title.trim() || !newTopic.content.trim()) return toast.error("Başlık ve içerik boş olamaz.");
    if (containsBadWord(newTopic.title) || containsBadWord(newTopic.content)) return toast.error("Uygunsuz kelime içeriyor!");

    try {
      const postData = {
        userId, username,
        title: newTopic.title,
        text: newTopic.content,
        category: newTopic.category,
        timestamp: serverTimestamp(),
        likedBy: [],
        commentCount: 0
      };
      const docRef = await addDoc(collection(db, "forumPosts"), postData);
      toast.success("Konu başarıyla açıldı!");
      setShowNewTopicModal(false);
      setNewTopic({ title: "", content: "", category: "Sohbet" });
      fetchPosts();
      await checkAndAssignBadges(userId);
    } catch (err) { toast.error("Konu açılamadı."); }
  };

  const handleAddComment = async () => {
    if (!isLoggedIn) return toast.error("Giriş yapmalısınız!");
    if (!newCommentText.trim()) return toast.error("Yorum boş olamaz!");
    if (containsBadWord(newCommentText)) return toast.error("Uygunsuz kelime içeriyor!");

    try {
      const commentData = {
        postId: selectedTopic.id, userId, username,
        content: newCommentText, createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "comments"), commentData);
      
      // Update comment count on post
      const postRef = doc(db, "forumPosts", selectedTopic.id);
      await updateDoc(postRef, { commentCount: (selectedTopic.commentCount || 0) + 1 });

      toast.success("Yorum eklendi!");
      setNewCommentText("");
      
      // Update local state
      const newComment = { id: docRef.id, ...commentData, createdAt: new Date() };
      const updatedPosts = posts.map(p => {
        if (p.id === selectedTopic.id) return { ...p, commentCount: (p.commentCount || 0) + 1 };
        return p;
      });
      setPosts(updatedPosts);
      setSelectedTopic({ ...selectedTopic, commentCount: (selectedTopic.commentCount || 0) + 1, comments: [...(selectedTopic.comments || []), newComment] });

      if (selectedTopic.userId !== userId) {
        await createNotification({
          userId: selectedTopic.userId, fromUserId: userId, fromUsername: username,
          type: "comment", postId: selectedTopic.id, commentId: docRef.id, commentText: newCommentText
        });
      }
      await checkAndAssignBadges(userId);
    } catch (err) { toast.error("Yorum eklenemedi."); }
  };

  const handleLike = async (postId) => {
    if (!isLoggedIn) return toast.error("Giriş yapmalısınız!");
    try {
      const post = posts.find((p) => p.id === postId);
      const postRef = doc(db, "forumPosts", postId);
      const isLiked = post.likedBy?.includes(userId);

      if (isLiked) {
        await updateDoc(postRef, { likedBy: arrayRemove(userId) });
        const update = p => p.id === postId ? { ...p, likedBy: p.likedBy.filter(id => id !== userId), likes: p.likes - 1 } : p;
        setPosts(posts.map(update));
        if (selectedTopic && selectedTopic.id === postId) setSelectedTopic(update(selectedTopic));
      } else {
        await updateDoc(postRef, { likedBy: arrayUnion(userId) });
        const update = p => p.id === postId ? { ...p, likedBy: [...(p.likedBy || []), userId], likes: p.likes + 1 } : p;
        setPosts(posts.map(update));
        if (selectedTopic && selectedTopic.id === postId) setSelectedTopic(update(selectedTopic));
        
        if (post.userId !== userId) {
          await createNotification({ userId: post.userId, fromUserId: userId, fromUsername: username, type: "like", postId });
        }
      }
    } catch (err) { toast.error("Beğeni işlemi başarısız."); }
  };

  const filteredPosts = posts.filter(p => {
    const matchCat = activeCategory === "Tümü" || p.category === activeCategory;
    const searchLow = searchQuery.toLowerCase();
    const matchSearch = (p.title || "").toLowerCase().includes(searchLow) || (p.text || "").toLowerCase().includes(searchLow);
    return matchCat && matchSearch;
  });

  return (
    <div className="forum-modern-page">
      {/* Background */}
      <div className="forum-bg-glow"></div>
      
      <div className="forum-modern-container">
        
        {view === "list" && (
          <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} className="forum-layout">
            
            {/* Sidebar: Categories */}
            <div className="forum-sidebar">
              <Button variant="primary" style={{width: '100%', marginBottom: 'var(--space-4)'}} onClick={() => setShowNewTopicModal(true)}>
                + Yeni Konu Aç
              </Button>
              <div className="category-list">
                <h3 className="category-header">Kategoriler</h3>
                {CATEGORIES.map(cat => (
                  <button 
                    key={cat} 
                    className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Area: Topics */}
            <div className="forum-main">
              <div className="forum-search-bar">
                <input 
                  type="text" 
                  className="input" 
                  placeholder="Konularda ara..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {loading ? (
                <div className="text-center py-8">Yükleniyor...</div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-8 text-secondary">Bu kategoride hiç konu bulunamadı.</div>
              ) : (
                <div className="topic-list">
                  {filteredPosts.map(post => (
                    <motion.div 
                      key={post.id} 
                      className="topic-card" 
                      onClick={() => handleOpenTopic(post)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="topic-card-left">
                        <div className="topic-avatar">
                           <Avatar avatarData={users[post.userId]?.avatar} seedFallback={post.username} size={48} />
                        </div>
                      </div>
                      <div className="topic-card-body">
                        <h4 className="topic-title">{post.title}</h4>
                        <div className="topic-meta">
                          <span className="topic-author">@{post.username}</span>
                          <span className="topic-date">{formatDateTime(post.timestamp)}</span>
                          <span className="badge badge-accent" style={{fontSize: '0.7rem'}}>{post.category}</span>
                        </div>
                      </div>
                      <div className="topic-card-stats">
                        <div className="stat"><span>💬</span> {post.commentCount || 0}</div>
                        <div className="stat"><span>❤️</span> {post.likes}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {view === "topic" && selectedTopic && (
          <motion.div initial={{opacity:0, x:20}} animate={{opacity:1, x:0}} className="topic-detail-view">
            <Button variant="ghost" onClick={() => { setView("list"); setSelectedTopic(null); }} style={{marginBottom: 'var(--space-4)'}}>
              ← Geri Dön
            </Button>
            
            {/* Original Post */}
            <div className="topic-entry main-entry">
              <div className="entry-sidebar">
                <div className="entry-avatar">
                   <Avatar avatarData={users[selectedTopic.userId]?.avatar} seedFallback={selectedTopic.username} size={64} />
                </div>
                <div className="entry-author">@{selectedTopic.username}</div>
              </div>
              <div className="entry-content-wrap">
                <div className="entry-header">
                  <h2 className="entry-title">{selectedTopic.title}</h2>
                  <span className="entry-date">{formatDateTime(selectedTopic.timestamp)}</span>
                </div>
                <div className="entry-body">
                  {selectedTopic.text}
                </div>
                <div className="entry-footer">
                  <span className="badge badge-accent">{selectedTopic.category}</span>
                  <button className={`btn-like ${selectedTopic.likedBy?.includes(userId) ? 'liked' : ''}`} onClick={() => handleLike(selectedTopic.id)}>
                    {selectedTopic.likedBy?.includes(userId) ? '❤️' : '🤍'} Beğen ({selectedTopic.likes})
                  </button>
                </div>
              </div>
            </div>

            <h3 className="comments-header">Yorumlar ({selectedTopic.comments ? selectedTopic.comments.length : (selectedTopic.commentCount || 0)})</h3>

            {/* Comments List */}
            <div className="comments-list">
              {!selectedTopic.comments ? (
                <div className="text-center py-4 text-secondary text-sm">Yorumlar yükleniyor...</div>
              ) : selectedTopic.comments.length === 0 ? (
                <p className="text-secondary text-sm text-center py-4">İlk yorumu sen yap!</p>
              ) : (
                selectedTopic.comments.map(c => (
                  <div key={c.id} className="topic-entry comment-entry">
                    <div className="entry-sidebar">
                      <div className="entry-avatar">
                         <Avatar avatarData={users[c.userId]?.avatar} seedFallback={c.username} size={48} />
                      </div>
                      <div className="entry-author">@{c.username}</div>
                    </div>
                    <div className="entry-content-wrap">
                      <div className="entry-header">
                        <span className="entry-date">{formatDateTime(c.createdAt)}</span>
                      </div>
                      <div className="entry-body">{c.content}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply Box */}
            <div className="reply-box">
              <textarea 
                className="input" 
                rows={3} 
                placeholder="Cevabını yaz..."
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
              />
              <Button variant="primary" style={{marginTop: 'var(--space-2)'}} onClick={handleAddComment}>Yanıtla</Button>
            </div>

          </motion.div>
        )}

      </div>

      {/* New Topic Modal */}
      <AnimatePresence>
        {showNewTopicModal && (
          <div className="modal-overlay">
            <motion.div className="modal-content" initial={{ scale: 0.9, opacity:0 }} animate={{ scale: 1, opacity:1 }} exit={{ scale: 0.9, opacity:0 }}>
              <div className="modal-header">
                <h3>Yeni Konu Aç</h3>
                <button className="modal-close" onClick={() => setShowNewTopicModal(false)}>✕</button>
              </div>
              <div className="modal-body flex flex-col gap-4">
                <input 
                  type="text" 
                  className="input" 
                  placeholder="Konu Başlığı" 
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({...newTopic, title: e.target.value})}
                />
                <select 
                  className="input" 
                  value={newTopic.category}
                  onChange={(e) => setNewTopic({...newTopic, category: e.target.value})}
                >
                  {CATEGORIES.filter(c => c !== "Tümü").map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <textarea 
                  className="input" 
                  rows={6} 
                  placeholder="Konu içeriğini detaylıca anlat..."
                  value={newTopic.content}
                  onChange={(e) => setNewTopic({...newTopic, content: e.target.value})}
                />
                <Button variant="primary" onClick={handleCreateTopic}>Oluştur</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ForumPage;
